import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Alert, Divider, Icon, Modal, Space, Typography } from '@faceup/ui-base'

import { type ReactNode, useState } from 'react'

import { useClient } from '../../App/ClientProvider'

import FaceUpLogo from '../../assets/logo-fu.svg'
import { useMedia } from '../../mq'
import GreenShieldIcon from '../../Shared/assets/green-shield-icon.svg?react'

const messages = defineMessages('followUp.components.footer', [
  {
    id: 'systemSecured',
    defaultMessage: 'All communication is encrypted.',
    wrappers: { modal: true },
  },
  {
    id: 'systemSecuredAnonymous',
    defaultMessage:
      'All communication is anonymous and encrypted. <modal>Learn more about anonymity</modal>',
    wrappers: { modal: true },
  },
  { id: 'poweredBy', defaultMessage: 'System powered by' },
  { id: 'privacyPolicy', defaultMessage: 'Privacy Policy' },
  { id: 'anonymousMoreInfoTitle', defaultMessage: 'How to stay anonymous' },
  {
    id: 'anonymousMoreInfo0',
    defaultMessage:
      'Do not provide any personal information in the form unless you really want to.',
  },
  {
    id: 'anonymousMoreInfo1',
    defaultMessage: 'Do not submit a report using a work laptop, phone or other work device.',
  },
  {
    id: 'anonymousMoreInfo2',
    defaultMessage:
      "Don't submit a report while you're at work, on a work network or logged into a work account (for example, a Google account).",
  },
  {
    id: 'anonymousMoreInfo3',
    defaultMessage:
      'For added security when submitting a serious report, you can download and use the <link>Tor browser</link>.',
    wrappers: { link: true },
  },
] as const)

type FooterProps = {
  /** Whether to hide the FaceUp branding. */
  isBrandingHidden?: boolean
  /**
   * Whether the in-progress report is anonymous; drives the "system secured"
   * message variant. Defaults to the anonymous variant for non-report surfaces
   * (home, survey) that have no report-form state. The report branch feeds the
   * live value down from ReportFormContext via InstitutionLayout.
   */
  isAnonymous?: boolean
  hideSecuredBanner?: boolean
}

export const Footer = ({
  isBrandingHidden,
  isAnonymous = true,
  hideSecuredBanner = false,
}: FooterProps) => {
  const [anonymousInfoModalOpen, setAnonymousInfoModalOpen] = useState(false)
  const { client } = useClient()

  const mdDown = useMedia('mdDown')

  const modalWrapper = (content: ReactNode) => (
    <button
      type='button'
      key='modal-link'
      className='cursor-pointer border-0 bg-transparent p-0 font-semibold text-colorSuccess underline'
      onClick={() => setAnonymousInfoModalOpen(true)}
    >
      {content}
    </button>
  )

  return (
    <div className='flex flex-col'>
      {!hideSecuredBanner && (
        <div
          className='mx-auto mb-2.5'
          data-test={isAnonymous ? 'footer-secured-anonymous' : 'footer-secured-encrypted'}
        >
          <Alert
            type='success'
            message={
              isAnonymous ? (
                <FormattedMessage
                  message={messages.systemSecuredAnonymous}
                  wrappers={{ modal: modalWrapper }}
                />
              ) : (
                <FormattedMessage
                  message={messages.systemSecured}
                  wrappers={{ modal: modalWrapper }}
                />
              )
            }
          />
          <Modal
            open={anonymousInfoModalOpen}
            title={<FormattedMessage message={messages.anonymousMoreInfoTitle} />}
            onClose={() => setAnonymousInfoModalOpen(false)}
          >
            <div className='mb-1.5 flex items-center gap-3.5'>
              <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
              <FormattedMessage message={messages.anonymousMoreInfo0} />
            </div>
            <div className='mb-1.5 flex items-center gap-3.5'>
              <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
              <FormattedMessage message={messages.anonymousMoreInfo1} />
            </div>
            <div className='mb-1.5 flex items-center gap-3.5'>
              <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
              <FormattedMessage message={messages.anonymousMoreInfo2} />
            </div>
            <div className='mb-1.5 flex items-center gap-3.5'>
              <Icon component={GreenShieldIcon} style={{ fontSize: 20 }} />
              <div>
                <FormattedMessage
                  message={messages.anonymousMoreInfo3}
                  wrappers={{
                    link: content => (
                      <a
                        href='https://www.torproject.org/'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {content}
                      </a>
                    ),
                  }}
                />
              </div>
            </div>
          </Modal>
        </div>
      )}

      <footer className='bg-transparent px-2.5 py-6 text-center text-xs'>
        <Space align='center' size={mdDown ? 8 : 5} direction={mdDown ? 'vertical' : 'horizontal'}>
          <a
            href='https://www.faceup.com/en/privacy-policy'
            target='_blank'
            rel='noopener noreferrer'
          >
            <Typography.Text type='secondary' size='sm' underline>
              <FormattedMessage message={messages.privacyPolicy} />
            </Typography.Text>
          </a>

          {client !== 'app' && !isBrandingHidden && (
            <>
              {!mdDown && <Divider type='vertical' />}

              <Space>
                <Typography.Text type='secondary' size='sm'>
                  <FormattedMessage message={messages.poweredBy} />
                </Typography.Text>

                <a href='https://www.faceup.com/' target='_blank' rel='noopener noreferrer'>
                  <img className='max-h-5 w-auto' src={FaceUpLogo} alt='FaceUp' />
                </a>
              </Space>
            </>
          )}
        </Space>
      </footer>
    </div>
  )
}
